import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useShop } from "@/contexts/ShopContext";
import { languages } from "@/lib/i18n";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Link2, Unlink } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { INTEG } from "@/constants/testIds";
import UserManagementSection from "@/components/UserManagementSection";
import DocsDownloadsSection from "@/components/DocsDownloadsSection";

export default function Settings() {
  const { user } = useAuth();
  const { t, lang, setLang } = useLang();
  const { shops } = useShop();
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: integrations } = useQuery({
    queryKey: ["integrations"],
    queryFn: async () => (await api.get("/integrations/status")).data,
  });

  // Handle Google OAuth callback query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const g = params.get("google");
    if (!g) return;
    if (g === "connected") {
      toast.success(t("google_connected_toast"));
      qc.invalidateQueries();
    } else if (g === "denied" || g === "state-invalid" || g === "error") {
      toast.error(t("google_error_toast"));
    }
    navigate("/settings", { replace: true });
  }, [location.search, navigate, qc, t]);

  const items = [
    {
      key: "dataforseo",
      label: "DataForSEO",
      description: t("integration_dataforseo_hint"),
      configured: integrations?.dataforseo,
      extra: integrations?.dataforseo
        ? "Live SERP data · account must be verified in DataForSEO panel"
        : null,
    },
    {
      key: "emergent_llm",
      label: "Claude Sonnet 4.5 (Emergent LLM Key)",
      description: t("integration_llm_hint"),
      configured: integrations?.emergent_llm,
    },
    {
      key: "resend",
      label: `Resend · ${integrations?.sender_email || "sender not set"}`,
      description: t("integration_resend_hint"),
      configured: integrations?.resend,
    },
    {
      key: "google_oauth",
      label: t("google_integration"),
      description: t("integration_configured") + " · " + t("google_per_shop_hint"),
      configured: integrations?.google_oauth,
    },
    {
      key: "scheduler",
      label: `APScheduler · ${integrations?.scheduler_day?.toUpperCase() || "MON"} ${String(integrations?.scheduler_hour ?? 8).padStart(2, "0")}:00 UTC`,
      description: "Automatischer Wochen-Digest läuft im Hintergrund.",
      configured: integrations?.scheduler_enabled,
    },
  ];

  return (
    <div className="space-y-8 max-w-4xl rk-fade-in">
      <header>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          {t("nav_settings")}
        </h1>
      </header>

      <Section title={t("settings_account")}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full overflow-hidden border border-border grid place-items-center bg-secondary text-xl font-mono">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              user?.name?.[0]?.toUpperCase()
            )}
          </div>
          <div>
            <div className="font-medium">{user?.name}</div>
            <div className="text-sm text-muted-foreground font-mono">{user?.email}</div>
          </div>
        </div>
      </Section>

      <Section title={t("settings_language")}>
        <Select value={lang} onValueChange={setLang}>
          <SelectTrigger className="w-48 h-10 font-mono text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {languages.map((l) => (
              <SelectItem key={l.code} value={l.code} className="font-mono text-sm">
                {l.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Section>

      <ShopRegionSection shops={shops} qc={qc} />

      <Section title="DataForSEO Health Monitor">
        <div className="flex items-start justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            Live-Status der Ranking-API. Erkenne API-Fehler früh, überwache Credits
            und die Erfolgsrate der letzten 24h.
          </div>
          <Button asChild variant="outline" className="shrink-0" data-testid="open-dfs-health-btn">
            <a href="/settings/dataforseo-health">Öffnen →</a>
          </Button>
        </div>
      </Section>

      <Section title={t("settings_integrations")}>
        <ul className="space-y-2">
          {items.map((it) => (
            <li
              key={it.key}
              className="flex items-start justify-between border border-border rounded-md px-4 py-3"
            >
              <div className="flex items-start gap-3 min-w-0">
                {it.configured ? (
                  <CheckCircle2 className="h-4 w-4 text-[hsl(var(--chart-3))] shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                )}
                <div className="min-w-0">
                  <div className="font-medium text-sm">{it.label}</div>
                  <div className="text-xs font-mono text-muted-foreground">{it.description}</div>
                  {it.extra && (
                    <div className="text-[10px] font-mono text-muted-foreground mt-1">
                      {it.extra}
                    </div>
                  )}
                </div>
              </div>
              <div
                className={cn(
                  "text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded shrink-0",
                  it.configured
                    ? "bg-[hsl(var(--chart-3))]/15 text-[hsl(var(--chart-3))]"
                    : "bg-secondary text-muted-foreground",
                )}
              >
                {it.configured ? t("integration_configured") : t("integration_missing")}
              </div>
            </li>
          ))}
        </ul>
      </Section>

      <Section title={t("google_integration")}>
        {!integrations?.google_oauth ? (
          <div className="text-sm text-muted-foreground font-mono">
            {t("google_not_configured_hint")}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground mb-3">{t("google_per_shop_hint")}</p>
        )}
        <ul className="space-y-2 mt-3">
          {shops.map((s) => (
            <GoogleShopRow key={s.shop_id} shop={s} configured={!!integrations?.google_oauth} />
          ))}
        </ul>
      </Section>

      <Section title={t("settings_docs")}>
        <DocsDownloadsSection />
      </Section>

      <Section title={t("settings_users")}>
        <UserManagementSection />
      </Section>

      <Section title={t("settings_shops")}>
        <ul className="space-y-2">
          {shops.map((s, i) => (
            <li
              key={s.shop_id}
              className="flex items-center justify-between border border-border rounded-md px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: `hsl(var(--chart-${(i % 5) + 1}))` }}
                />
                <div>
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="text-xs font-mono text-muted-foreground">{s.domain}</div>
                </div>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                {s.country} · {s.industry} · {s.funnel_type}
              </div>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

function GoogleShopRow({ shop, configured }) {
  const { t } = useLang();
  const qc = useQueryClient();

  const { data: status } = useQuery({
    queryKey: ["google-status", shop.shop_id],
    queryFn: async () => (await api.get(`/integrations/google/status?shop_id=${shop.shop_id}`)).data,
  });

  const connect = useMutation({
    mutationFn: async () =>
      (await api.get(`/integrations/google/auth?shop_id=${shop.shop_id}`)).data,
    onSuccess: (data) => {
      if (data.configured && data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error(data.reason || t("google_not_configured_hint"));
      }
    },
  });

  const disconnect = useMutation({
    mutationFn: async () =>
      (await api.post(`/integrations/google/disconnect?shop_id=${shop.shop_id}`)).data,
    onSuccess: () => {
      toast.success(t("google_disconnect"));
      qc.invalidateQueries({ queryKey: ["google-status", shop.shop_id] });
    },
  });

  const connected = !!status?.connected;

  return (
    <li className="flex items-center justify-between border border-border rounded-md px-4 py-3">
      <div className="flex items-center gap-3 min-w-0">
        <div
          data-testid={INTEG.googleStatusBadge(shop.shop_id)}
          className={cn(
            "w-2 h-2 rounded-full",
            connected ? "bg-[hsl(var(--chart-3))]" : "bg-muted-foreground/50",
          )}
        />
        <div>
          <div className="font-medium text-sm">{shop.name}</div>
          <div className="text-xs font-mono text-muted-foreground">{shop.domain}</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "text-[10px] font-mono uppercase tracking-[0.18em]",
            connected ? "text-[hsl(var(--chart-3))]" : "text-muted-foreground",
          )}
        >
          {connected ? t("google_connected") : t("google_not_connected")}
        </span>
        {connected ? (
          <Button
            data-testid={INTEG.googleDisconnectBtn(shop.shop_id)}
            variant="outline"
            size="sm"
            onClick={() => disconnect.mutate()}
            className="gap-1.5"
          >
            <Unlink className="h-3.5 w-3.5" />
            {t("google_disconnect")}
          </Button>
        ) : (
          <Button
            data-testid={INTEG.googleConnectBtn(shop.shop_id)}
            size="sm"
            onClick={() => connect.mutate()}
            disabled={!configured}
            className="gap-1.5"
          >
            <Link2 className="h-3.5 w-3.5" />
            {t("google_connect")}
          </Button>
        )}
      </div>
    </li>
  );
}

function Section({ title, children }) {
  return (
    <section className="border border-border bg-card rounded-lg p-6">
      <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground mb-4">
        {title}
      </div>
      {children}
    </section>
  );
}

function ShopRegionSection({ shops, qc }) {
  const { data: countriesData } = useQuery({
    queryKey: ["countries"],
    queryFn: async () => (await api.get("/countries")).data,
  });
  const countries = countriesData?.countries || [];

  const regionMutation = useMutation({
    mutationFn: async ({ shopId, code }) =>
      (await api.patch(`/shops/${shopId}/region`, { country_code: code })).data,
    onSuccess: (data, vars) => {
      toast.success(`Region auf ${data.preset.flag} ${data.preset.name} geändert. Nächster Retest nutzt neue Location.`);
      qc.invalidateQueries();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Fehler beim Setzen der Region"),
  });

  return (
    <Section title="Ranking-Region pro Shop (Multi-Region Tracking)">
      <div className="space-y-3">
        <div className="text-sm text-muted-foreground">
          Wähle in welchem Land Rankly Google-Positionen tracken soll. Änderungen
          wirken beim nächsten Retest.
        </div>
        {shops?.map((shop) => {
          const currentCode = countries.find(
            (c) => c.location_code === (shop.location_code ?? 2276)
          )?.code || "DE";
          return (
            <div
              key={shop.shop_id}
              className="flex items-center justify-between gap-4 border border-border rounded-md px-4 py-3"
              data-testid={`shop-region-row-${shop.shop_id}`}
            >
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{shop.name}</div>
                <div className="text-xs font-mono text-muted-foreground truncate">
                  {shop.domain}
                </div>
              </div>
              <Select
                value={currentCode}
                onValueChange={(code) => regionMutation.mutate({ shopId: shop.shop_id, code })}
                disabled={regionMutation.isPending}
              >
                <SelectTrigger className="w-48 h-9 font-mono text-sm" data-testid={`region-select-${shop.shop_id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code} className="font-mono text-sm">
                      {c.flag} {c.name} ({c.language_code.toUpperCase()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

